import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDate } from '../utils/dateFormat';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { 
  FileText, 
  Upload, 
  RefreshCw, 
  CheckCircle, 
  AlertTriangle,
  Loader2,
  Calendar,
  User,
  FolderOpen
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const RestoreMissingDocumentsPage = () => {
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [totalMissing, setTotalMissing] = useState(0);
  const [totalRestored, setTotalRestored] = useState(0);
  const [statusFilter, setStatusFilter] = useState('missing');
  const [uploadingId, setUploadingId] = useState(null);
  const fileInputRefs = useRef({});

  useEffect(() => {
    fetchDocuments();
  }, [statusFilter]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/missing-documents?status_filter=${statusFilter}`);
      setDocuments(response.data.documents || []);
      setTotalMissing(response.data.total_missing || 0);
      setTotalRestored(response.data.total_restored || 0);
    } catch (error) {
      toast.error('Failed to load documents');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (docId, file) => {
    if (file) {
      handleRestore(docId, file);
    }
  };

  const handleRestore = async (docId, file) => {
    setUploadingId(docId);
    
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API}/admin/restore-document/${docId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Document restored successfully');
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore document');
    } finally {
      setUploadingId(null);
    }
  };

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return 'N/A';
    return formatDate(dateStr) || 'N/A';
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Restore Missing Documents</h1>
          <p className="text-muted-foreground mt-1">
            Re-upload files for documents that are missing from storage
          </p>
        </div>
        <Button variant="outline" onClick={fetchDocuments} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Progress Counter */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-8">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                <span className="text-2xl font-bold">{totalMissing}</span>
                <span className="text-muted-foreground">Missing</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-2xl font-bold">{totalRestored}</span>
                <span className="text-muted-foreground">Restored</span>
              </div>
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-500" />
                <span className="text-2xl font-bold">{totalMissing + totalRestored}</span>
                <span className="text-muted-foreground">Total</span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-muted-foreground">Progress</div>
              <div className="text-lg font-semibold">
                {totalMissing + totalRestored > 0 
                  ? Math.round((totalRestored / (totalMissing + totalRestored)) * 100) 
                  : 0}% Complete
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filter Tabs */}
      <Tabs value={statusFilter} onValueChange={setStatusFilter} className="mb-6">
        <TabsList>
          <TabsTrigger value="missing" className="gap-2">
            <AlertTriangle className="w-4 h-4" />
            Missing ({totalMissing})
          </TabsTrigger>
          <TabsTrigger value="restored" className="gap-2">
            <CheckCircle className="w-4 h-4" />
            Restored ({totalRestored})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Documents List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : documents.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium">
              {statusFilter === 'missing' 
                ? 'No missing documents!' 
                : 'No restored documents yet'}
            </h3>
            <p className="text-muted-foreground mt-1">
              {statusFilter === 'missing'
                ? 'All documents have been restored or are stored correctly.'
                : 'Restore some missing documents to see them here.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {documents.map((doc) => (
            <Card key={doc.id} className={doc.status === 'restored' ? 'border-green-200 bg-green-50/30' : ''}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                      <h3 className="font-medium truncate">{doc.document_name}</h3>
                      <Badge variant={doc.status === 'missing' ? 'destructive' : 'default'} className={doc.status === 'restored' ? 'bg-green-500' : ''}>
                        {doc.status === 'missing' ? 'Missing file' : 'Restored'}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <FolderOpen className="w-4 h-4" />
                        <span className="truncate">Case: {doc.case_id}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        <span className="truncate">{doc.participant_name || 'Unknown'}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDateDisplay(doc.created_at)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        <span className="truncate">By: {doc.created_by_name || 'Unknown'}</span>
                      </div>
                    </div>

                    {doc.original_filename && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Original file: {doc.original_filename}
                      </p>
                    )}

                    {doc.restored_at && (
                      <p className="text-xs text-green-600 mt-2">
                        Restored on {formatDateDisplay(doc.restored_at)} by {doc.restored_by_name}
                      </p>
                    )}
                  </div>

                  <div className="flex-shrink-0">
                    {doc.status === 'missing' ? (
                      <div className="flex items-center gap-2">
                        <Input
                          type="file"
                          ref={(el) => fileInputRefs.current[doc.id] = el}
                          onChange={(e) => handleFileSelect(doc.id, e.target.files?.[0])}
                          className="hidden"
                          id={`file-${doc.id}`}
                          data-testid={`file-input-${doc.id}`}
                        />
                        <Button
                          onClick={() => fileInputRefs.current[doc.id]?.click()}
                          disabled={uploadingId === doc.id}
                          data-testid={`upload-btn-${doc.id}`}
                        >
                          {uploadingId === doc.id ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload className="w-4 h-4 mr-2" />
                              Upload
                            </>
                          )}
                        </Button>
                      </div>
                    ) : (
                      <CheckCircle className="w-6 h-6 text-green-500" />
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default RestoreMissingDocumentsPage;
